import { moneyFormat } from "@redotech/money/util";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { isDoneStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { Parcel } from "@redotech/redo-model/outbound-labels/parcel";
import { Rate } from "@redotech/redo-model/shipment";
import { LRUCache } from "@redotech/util/cache";
import { formatParcelName } from "../setting/parcels";

export function canRequestGetNewRates(group: FulfillmentGroupData): boolean {
  if (isDoneStatus(group.status)) {
    return false;
  }
  const twoWeeksAgo = Temporal.Now.instant().subtract({ hours: 24 * 7 * 2 });
  const someRateIsOld = group.availableShipmentRates?.rates.some(
    (rate) =>
      new Date(rate.created_at).getTime() < twoWeeksAgo.epochMilliseconds,
  );
  if (someRateIsOld) {
    return true;
  }

  const minuteAgo = Temporal.Now.instant().subtract({
    minutes: 1,
  }).epochMilliseconds;
  const ratesPendingTooLong =
    group.ratesPendingSince && group.ratesPendingSince.getTime() < minuteAgo;

  if (ratesPendingTooLong) {
    return true;
  }
  return false;
}

export function getPriceWithUpcharge(rate: Rate): number {
  return +(rate?.rate ?? 0);
}

export const isSplit = (row: FulfillmentGroupData) => {
  // TODO: figure this out
  return false;
};

export const isMerged = (row: FulfillmentGroupData) => {
  // TODO: figure this out too
  return false;
};

export const getOrderName = (row: FulfillmentGroupData): string => {
  return row.orders.map((order) => order.name).join(", ");
};

export const getOrderAge = (row: FulfillmentGroupData): string => {
  const orderDate = getOrderDate(row);

  if (!orderDate) {
    return "";
  }

  const now = Temporal.Now.instant();
  const orderInstant = Temporal.Instant.from(orderDate.toISOString());
  const diff = now.since(orderInstant);

  const totalMinutes = diff.total({ unit: "minutes" });
  const totalHours = diff.total({ unit: "hours" });
  const totalDays = diff.total({ unit: "days" });

  if (totalMinutes < 60) {
    const minutes = Math.floor(totalMinutes);
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }

  if (totalHours < 48) {
    const hours = Math.floor(totalHours);
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  const days = Math.floor(totalDays);
  return `${days} day${days === 1 ? "" : "s"}`;
};

export const getCustomerName = (row: FulfillmentGroupData): string => {
  return row.customerName ?? "";
};

export const getRecipientName = (row: FulfillmentGroupData): string => {
  return row.destinationAddress?.name ?? "";
};

export const getOrderDate = (
  row: FulfillmentGroupData,
): Date | undefined | null => {
  return row?.orderCreatedAt;
};

export const getShipmentCreatedAt = (row: FulfillmentGroupData): Date => {
  return row.providerCreatedAt;
};

export const getOrderTotal = (row: FulfillmentGroupData) => {
  return row.totalPrice.amount;
};

export const getOrderCurrency = (row: FulfillmentGroupData) => {
  return row.totalPrice.currency;
};

export const getNumItems = (row: FulfillmentGroupData) => {
  return row.totalItemQuantity;
};

export const getCustomerShippingMethod = (
  row: FulfillmentGroupData | undefined,
): string => {
  return row?.shippingMethodNames[0] ?? "";
};

export const getCustomerShippingPrice = (row: FulfillmentGroupData) => {
  return moneyFormat({
    amount: +row.totalShippingCost.amount,
    currency: row.totalShippingCost.currency,
  });
};

export const getParcelName = (parcel: Parcel) => {
  return "carrier" in parcel ? formatParcelName(parcel.name) : parcel.name;
};

export const formatDateString = (
  isoDate: string | undefined,
  ignoreLocale = false,
): string => {
  if (!isoDate) {
    return "";
  }
  try {
    return new Date(isoDate).toLocaleString("default", {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(ignoreLocale && { timeZone: "UTC" }),
    });
  } catch (e) {
    console.error(e);
    return "";
  }
};

const expandedItemsCache = new LRUCache<
  FulfillmentGroupData,
  FulfillmentGroupData["items"]
>(200);

export function getExpandedItems(
  group: FulfillmentGroupData,
): FulfillmentGroupData["items"] {
  const cached = expandedItemsCache.get(group);
  if (cached) {
    return cached;
  }

  const expanded = group.items.flatMap((item) =>
    Array.from({ length: item.quantity }, () => ({
      ...item,
      quantity: 1,
      totalQuantity: 1,
    })),
  );

  expandedItemsCache.set(group, expanded);
  return expanded;
}
